import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import GoogleSignInButton, { GOOGLE_SIGNIN_ENABLED } from '@/components/GoogleSignInButton';
import OAuthIconButton, { OAUTH_PROVIDER_ENABLED } from '@/components/OAuthProviderButtons';

const normalizeEmail = (value) => (value || '').trim().toLowerCase();

// Friendly, live client-side email check. Returns '' when the value is empty or
// looks valid, otherwise a short human-readable hint. Deliberately mirrors (but
// softens) the backend's own validation so the two rarely disagree.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validateEmailFriendly = (email) => {
  const value = (email || '').trim();
  if (!value) return '';
  const at = value.indexOf('@');
  if (at === -1) return 'Add an @ — like you@company.com';
  if (value.indexOf('@', at + 1) !== -1) return 'An email can only have one @';
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!local) return 'Add the part before @ — like you@company.com';
  if (!domain) return 'Add a domain after @ — like company.com';
  // Catch weird special characters ourselves (";", ",", ":", etc.) so the
  // browser's native validation bubble never has to.
  const badLocal = local.match(/[^A-Za-z0-9._%+-]/);
  if (badLocal) return `Remove the "${badLocal[0]}" from your email`;
  const badDomain = domain.match(/[^A-Za-z0-9.-]/);
  if (badDomain) return `Remove the "${badDomain[0]}" from your email`;
  if (!domain.includes('.')) return `The part after @ needs a period — try ${domain}.com`;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
    return "That domain doesn't look right";
  }
  if (!EMAIL_RE.test(value)) return "That email doesn't look right";
  return '';
};

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedRole = location.state?.role;
  const preselectedEntityType = location.state?.entityType;
  const prefilledEmail = location.state?.prefilledEmail || '';
  const prefilledName = location.state?.prefilledName || '';
  const returnTo = location.state?.returnTo;
  const initialMode = location.state?.mode || (location.pathname === '/signup' ? 'signup' : undefined); // 'login' or 'signup'
  const isProgramApplyFlow = typeof returnTo === 'string' && returnTo.startsWith('/startup/programs?apply=');

  const [isLogin, setIsLogin] = useState(initialMode ? initialMode === 'login' : !preselectedRole);
  const [formData, setFormData] = useState({
    email: normalizeEmail(prefilledEmail),
    password: '',
    full_name: prefilledName,
    role: preselectedRole || 'startup',
    entity_type: preselectedEntityType || (preselectedRole === 'vc' ? 'vc' : 'startup')
  });
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [signupSentTo, setSignupSentTo] = useState('');
  const [showResendVerify, setShowResendVerify] = useState(false);
  const [resending, setResending] = useState(false);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthProfile, setOauthProfile] = useState(null); // { provider, pendingToken, email, full_name }
  const [oauthRole, setOauthRole] = useState({ role: 'startup', entity_type: 'startup' });
  const [forgotMode, setForgotMode] = useState(Boolean(location.state?.forgotPassword));
  const [forgotEmail, setForgotEmail] = useState(normalizeEmail(prefilledEmail));
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [authTransition, setAuthTransition] = useState('none');

  const openForgotPassword = () => {
    setAuthTransition('forgot-left');
    setForgotEmail(normalizeEmail(formData.email));
    setForgotSent(false);
    setForgotMode(true);
  };

  const closeForgotPassword = () => {
    setAuthTransition('signin-right');
    setForgotMode(false);
    setForgotSent(false);
  };

  const toggleAuthMode = () => {
    setAuthTransition(isLogin ? 'signup-up' : 'signin-down');
    setEmailError('');
    setEmailTouched(false);
    setIsLogin(!isLogin);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = normalizeEmail(forgotEmail);
    if (!email) return;

    const emailErr = validateEmailFriendly(email);
    if (emailErr) {
      toast.error(emailErr);
      return;
    }

    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setForgotEmail(email);
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset link');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = normalizeEmail(formData.email);
    if (!email) return;
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success('Verification email sent. Check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend');
    } finally {
      setResending(false);
    }
  };

  const PRE_DASHBOARD_LAST_SEEN_PREFIX = 'founderboard_pre_dashboard_last_seen:';
  const PRE_DASHBOARD_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

  const getPreDashboardStorageKey = (account) => {
    const identity = account?._id || account?.id || account?.email || account?.username || 'default';
    return `${PRE_DASHBOARD_LAST_SEEN_PREFIX}${identity}`;
  };

  const shouldShowPreDashboard = (account) => {
    const raw = localStorage.getItem(getPreDashboardStorageKey(account));
    if (!raw) return true;

    const lastSeen = Number(raw);
    if (!Number.isFinite(lastSeen)) return true;

    return Date.now() - lastSeen >= PRE_DASHBOARD_COOLDOWN_MS;
  };

  // Shared "we now have a token + user" tail — used by password login/signup
  // AND Google sign-in/sign-up, so both land on the same routing logic.
  const completeAuth = (responseData, { successToast, isSignup = false } = {}) => {
    const userData = responseData.user;
    if (userData.role === 'vc' && !userData.entity_type) {
      const pending = localStorage.getItem('pending_entity_type');
      if (pending) {
        userData.entity_type = pending;
        localStorage.removeItem('pending_entity_type');
      }
    }
    localStorage.setItem('token', responseData.token);
    localStorage.setItem('user', JSON.stringify(userData));

    if (successToast) toast.success(successToast);

    if (isSignup && responseData.joined_workspace) {
      const jw = responseData.joined_workspace;
      setUser(userData);
      toast.success(`Welcome to ${jw.fund_name}!`);
      window.location.href = jw.kind === 'fund' ? '/vc/chat' : '/startup/dashboard';
      return;
    }

    const u = userData;
    const isTeamInvite = returnTo && returnTo.startsWith('/accept-team-invite');
    if (!u.onboarding_complete && !isTeamInvite) {
      // Onboarding still needs the in-memory user immediately because the signup
      // routes are guarded by App.js.
      setUser(userData);
      navigate(u.role === 'vc' ? '/signup/vc' : '/signup/startup', { state: returnTo ? { returnTo } : undefined });
      return;
    }

    // Completed accounts only see the pre-dashboard on a normal dashboard entry
    // when they have not seen it in the last 7 days. Deep-link returnTo flows keep
    // going directly to their intended destination instead of being interrupted.
    const defaultDestination = u.role === 'vc' ? '/vc/chat' : '/startup/dashboard';
    const destination = returnTo || defaultDestination;

    if (!returnTo && shouldShowPreDashboard(u)) {
      sessionStorage.setItem('pre_dashboard_destination', destination);
      window.location.assign('/pre-dashboard');
      return;
    }

    sessionStorage.removeItem('pre_dashboard_destination');
    window.location.assign(destination);
  };

  // Shared by Google (same-page ID token) AND Microsoft/Zoho (full-page redirect,
  // resolved below via the /oauth/callback/:provider -> location.state hand-off).
  // Either an existing account logs straight in, or a brand-new email needs a
  // role first — the backend hands back a short-lived pending_token for that.
  const handleOAuthResponse = (provider, data) => {
    if (data.new_user) {
      setOauthRole({ role: 'startup', entity_type: 'startup' });
      setOauthProfile({ provider, pendingToken: data.pending_token, email: data.email, full_name: data.full_name });
    } else {
      completeAuth(data, { successToast: 'Logged in successfully', isSignup: false });
    }
  };

  const handleGoogleCredential = async (credential) => {
    setOauthBusy(true);
    try {
      const response = await api.post('/auth/google', { credential });
      handleOAuthResponse('google', response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setOauthBusy(false);
    }
  };

  // Resolve a Microsoft/Zoho redirect that just bounced back from OAuthCallback.jsx
  useEffect(() => {
    if (!location.state?.oauthResult) return;
    const { oauthResult, oauthProvider } = location.state;
    handleOAuthResponse(oauthProvider, oauthResult);
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finishOAuthSignup = async () => {
    if (!oauthProfile) return;
    setOauthBusy(true);
    try {
      const response = await api.post('/auth/oauth/complete', {
        pending_token: oauthProfile.pendingToken,
        role: oauthRole.role,
        entity_type: oauthRole.entity_type,
      });
      toast.success('Account created');
      completeAuth(response.data, { successToast: 'Logged in', isSignup: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sign-in failed');
    } finally {
      setOauthBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard the email ourselves (type="text" means the browser no longer does).
    const emailErr = validateEmailFriendly(normalizeEmail(formData.email));
    if (emailErr) {
      setEmailError(emailErr);
      setEmailTouched(true);
      return;
    }
    setLoading(true);
    setShowResendVerify(false);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const email = normalizeEmail(formData.email);
      const payload = isLogin 
        ? { email, password: formData.password }
        : { email, password: formData.password, full_name: formData.full_name, role: formData.role };

      // AUTO-VERIFY path: if user is signing up via an invite link, pass the token so backend
      // can skip the verification email. Invite ownership already proves the inbox is theirs.
      let registerUrl = endpoint;
      if (!isLogin && returnTo) {
        const teamMatch = returnTo.match(/^\/accept-team-invite\/([^/?#]+)/);
        const capMatch = returnTo.match(/^\/accept-invite\/([^/?#]+)/);
        if (teamMatch) registerUrl += `?team_invite_token=${encodeURIComponent(teamMatch[1])}`;
        else if (capMatch) registerUrl += `?cap_table_invite_token=${encodeURIComponent(capMatch[1])}`;
      }

      const response = await api.post(registerUrl, payload);
      
      // SIGNUP flow:
      //  - If backend auto-verified (invite-based signup), proceed straight to auto-login + returnTo
      //  - Otherwise, show "Check your inbox" verification gate
      if (!isLogin) {
        const wasAutoVerified = response.data.auto_verified_via || response.data.user?.email_verified;
        if (!wasAutoVerified) {
          setSignupSentTo(email);
          if (formData.role === 'vc') {
            localStorage.setItem('pending_entity_type', formData.entity_type);
          }
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          toast.success('Check your inbox to verify your email.');
          setLoading(false);
          return;
        }
        // else fall through to the post-login routing block below
        toast.success('Account created');
      }

      completeAuth(response.data, { successToast: isLogin ? 'Logged in successfully' : 'Logged in', isSignup: !isLogin });
    } catch (error) {
      const data = error.response?.data;
      const detail = data?.detail || 'Authentication failed';
      const emailIssue = Array.isArray(data?.detail_raw)
        && data.detail_raw.some((e) => Array.isArray(e?.loc) && e.loc.includes('email'));
      if (emailIssue) {
        // 422 email-format rejection — surface it inline under the field, not as a toast.
        setEmailError(validateEmailFriendly(formData.email) || "That email doesn't look right");
        setEmailTouched(true);
      } else if (typeof detail === 'string' && detail.startsWith('UNVERIFIED_EMAIL')) {
        toast.error('Please verify your email first.');
        setShowResendVerify(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Gentle display: only show the error once there's an @ or the field has been blurred.
  const showEmailError = !!emailError && (emailTouched || formData.email.includes('@'));
  const authViewKey = forgotMode
    ? (forgotSent ? 'forgot-sent' : 'forgot')
    : oauthProfile
      ? 'oauth-profile'
      : signupSentTo
        ? 'signup-sent'
        : (isLogin ? 'login' : 'signup');

  return (
    <div className="min-h-[100dvh] w-full overflow-x-hidden bg-[#0d0b18] lg:h-screen lg:min-h-0 lg:overflow-hidden lg:bg-white" data-testid="founder-auth-page">
      <div className="flex min-h-[100dvh] w-full flex-col bg-[#0d0b18] lg:grid lg:h-screen lg:min-h-0 lg:grid-cols-[60%_40%] lg:overflow-hidden lg:bg-white">
        {/* Brand / product story — inspired by the supplied reference, but kept FounderBoard-native. */}
        <aside className="relative flex h-[184px] shrink-0 flex-col overflow-hidden bg-[#0d0b18] px-6 pb-3 pt-4 text-white sm:h-[194px] sm:px-8 sm:pb-4 sm:pt-5 lg:h-screen lg:min-h-0 lg:px-12 lg:py-8 xl:px-16 xl:py-10 2xl:px-20">
          <div className="pointer-events-none absolute -left-44 top-[42%] hidden h-[520px] w-[520px] rounded-full border border-violet-400/20 lg:block" />
          <div className="pointer-events-none absolute -left-32 top-[46%] hidden h-[420px] w-[420px] rounded-full border border-emerald-400/15 lg:block" />
          <div className="pointer-events-none absolute -right-28 -top-28 hidden h-72 w-72 rounded-full bg-violet-600/10 blur-3xl lg:block" />

          <Link to="/" className="relative z-10 mx-auto inline-flex w-fit items-center gap-2.5 lg:mx-0 lg:gap-3" aria-label="FounderBoard home">
            <img
              src="/founderboard-logo.png"
              alt="FounderBoard"
              className="h-7 w-7 rounded-md object-contain lg:h-9 lg:w-9 lg:rounded-lg"
            />
            <span className="font-heading text-[13px] font-bold tracking-[-0.2px] lg:text-[15px]">FounderBoard</span>
          </Link>

          <div className="relative z-10 flex flex-1 items-center w-full pb-0 lg:my-auto lg:block lg:flex-none lg:max-w-[620px] lg:py-6 xl:py-8">
            <div className="grid w-full grid-cols-2 items-stretch gap-7 sm:gap-10 lg:block">
              <h1 className="flex min-h-[92px] items-center text-left font-heading text-[30px] font-black uppercase leading-[0.88] tracking-[-0.045em] sm:min-h-[102px] sm:text-[34px] lg:min-h-0 lg:block lg:text-[50px] lg:font-extrabold lg:leading-[0.96] xl:text-[58px] 2xl:text-[64px]">
                <span className="lg:hidden">Build.<br />Fund.<br />Scale.</span>
                <span className="hidden lg:inline">Build.<br />Fund.<br />Scale.</span>
              </h1>
              <div className="ml-auto flex min-h-[92px] max-w-[184px] flex-col justify-center border-l border-white/10 pl-4 text-right sm:min-h-[102px] sm:max-w-[228px] sm:pl-5 lg:hidden">
                <p className="font-heading text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">One workspace</p>
                <p className="mt-1.5 font-heading text-[13px] font-semibold leading-[1.35] tracking-[-0.02em] text-slate-100 sm:text-[14px]">
                  For founders,<br />investors and<br />incubators.
                </p>
              </div>
            </div>
            <p className="mt-3 hidden max-w-[500px] text-[11px] leading-[1.5] text-slate-400 sm:text-xs lg:mt-5 lg:block lg:max-w-[520px] lg:text-[13px] lg:leading-6 xl:text-[14px]">
              One workspace for founders, investors and incubators — from first profile to ongoing updates.
            </p>

            <div className="mt-6 hidden lg:block lg:space-y-3.5 xl:mt-8 xl:space-y-4">
              {[
                ['01', 'Startup profile', 'Keep your story, metrics and documents in one place.'],
                ['02', 'Programs & capital', 'Apply to opportunities without rebuilding your application.'],
                ['03', 'Monthly updates', 'Share structured progress with the people backing you.'],
                ['04', 'AI coach', 'Turn business context into clearer next actions.'],
              ].map(([number, title, description]) => (
                <div key={number} className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-emerald-400/20 bg-emerald-400/10 text-[9px] font-bold text-emerald-300 xl:h-7 xl:w-7 xl:text-[10px]">
                    {number}
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-white xl:text-[13px]">{title}</p>
                    <p className="mt-0.5 max-w-[470px] text-[10px] leading-[1.5] text-slate-500 xl:mt-1 xl:text-[11px]">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="relative z-10 hidden text-[10px] text-slate-600 lg:block">FounderBoard © 2026</p>
        </aside>

        {/* Authentication side */}
        <main className="relative -mt-px flex min-h-0 flex-1 items-start justify-start overflow-visible rounded-t-[28px] border-t border-slate-200 bg-white px-6 pb-4 pt-5 lg:mt-0 lg:h-screen lg:min-h-0 lg:items-start lg:justify-center lg:overflow-y-auto lg:rounded-none lg:border-t-0 lg:px-10 lg:py-10 xl:px-14 xl:py-12 2xl:px-20">
          <div className="mx-auto w-full max-w-[342px] lg:my-auto lg:max-w-[430px] 2xl:max-w-[450px]">
            {/* Mobile logo — desktop branding lives in the left panel. */}
            <Link to="/" className="hidden" aria-label="FounderBoard home">
              <img src="/founderboard-logo.png" alt="FounderBoard" className="h-9 w-9 rounded-lg object-contain" />
              <span className="font-heading text-sm font-bold tracking-[-0.2px] text-[#111118]">FounderBoard</span>
            </Link>

            <style>{`
              @keyframes founderboardAuthSignupUp {
                0% { opacity: 0; transform: translate3d(0, 110px, 0) scale(.992); filter: blur(3px); }
                55% { opacity: 1; filter: blur(.5px); }
                100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
              }
              @keyframes founderboardAuthSigninDown {
                0% { opacity: 0; transform: translate3d(0, -110px, 0) scale(.992); filter: blur(3px); }
                55% { opacity: 1; filter: blur(.5px); }
                100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
              }
              @keyframes founderboardAuthForgotLeft {
                0% { opacity: 0; transform: translate3d(130px, 0, 0) scale(.992); filter: blur(3px); }
                55% { opacity: 1; filter: blur(.5px); }
                100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
              }
              @keyframes founderboardAuthSigninRight {
                0% { opacity: 0; transform: translate3d(-130px, 0, 0) scale(.992); filter: blur(3px); }
                55% { opacity: 1; filter: blur(.5px); }
                100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
              }
              .founderboard-auth-transition {
                animation-duration: 480ms;
                animation-timing-function: cubic-bezier(.22, 1, .36, 1);
                animation-fill-mode: both;
                transform-origin: 50% 25%;
                will-change: transform, opacity, filter;
              }
              .founderboard-auth-transition[data-transition="signup-up"] {
                animation-name: founderboardAuthSignupUp;
              }
              .founderboard-auth-transition[data-transition="signin-down"] {
                animation-name: founderboardAuthSigninDown;
              }
              .founderboard-auth-transition[data-transition="forgot-left"] {
                animation-name: founderboardAuthForgotLeft;
              }
              .founderboard-auth-transition[data-transition="signin-right"] {
                animation-name: founderboardAuthSigninRight;
              }
              .founderboard-auth-transition > .auth-heading {
                animation: founderboardAuthHeading 500ms cubic-bezier(.22, 1, .36, 1) both;
              }
              @keyframes founderboardAuthHeading {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @media (prefers-reduced-motion: reduce) {
                .founderboard-auth-transition,
                .founderboard-auth-transition > .auth-heading { animation: none !important; }
              }
            `}</style>

            <div
              key={authViewKey}
              className="founderboard-auth-transition"
              data-transition={authTransition}
            >
            <div className="auth-heading mb-4 lg:mb-9">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-600 lg:mb-2 lg:text-[11px] lg:tracking-[0.16em]">
                {forgotMode ? 'Account Recovery' : (isLogin ? 'FounderBoard Access' : 'Join FounderBoard')}
              </p>
              <h2 className="font-heading text-xl font-extrabold leading-[25px] tracking-[-0.025em] text-[#111118] lg:text-[38px] lg:font-bold lg:leading-[normal] lg:tracking-[-0.035em]">
                {forgotMode ? (forgotSent ? 'Check your inbox' : 'Reset your password') : (signupSentTo ? 'Check your inbox' : (isLogin ? 'Welcome back' : 'Create account'))}
              </h2>
              <p className="mt-0.5 text-xs leading-4 text-slate-500 lg:mt-2 lg:text-[13px] lg:leading-5">
                {forgotMode
                  ? (forgotSent
                    ? `If ${forgotEmail} matches an account, we sent a reset link. It expires in 1 hour.`
                    : 'Enter your email and we’ll send you a password reset link.')
                  : (signupSentTo
                    ? `We sent a verification link to ${signupSentTo}.`
                    : (isLogin ? 'Sign in to continue to your FounderBoard workspace.' : 'Create your account and choose how you use FounderBoard.'))}
              </p>
            </div>

            {forgotMode ? (
              forgotSent ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm" data-testid="forgot-sent-card">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl text-emerald-600">✓</div>
                  <p className="text-sm leading-6 text-slate-500">
                    If <span className="font-semibold text-slate-900">{forgotEmail}</span> matches an account, we sent a reset link. It expires in 1 hour.
                  </p>
                  <button
                    type="button"
                    onClick={closeForgotPassword}
                    className="mt-6 text-sm font-semibold text-violet-600 hover:text-violet-700"
                    data-testid="forgot-back-login"
                  >
                    ← Back to login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-3 lg:space-y-5" data-testid="forgot-form">
                  <div>
                    <label className="mb-1 block text-[10px] font-bold uppercase leading-[13px] tracking-[0.08em] text-slate-600 lg:mb-1.5 lg:leading-[normal] lg:tracking-[0.13em]">
                      Email address
                    </label>
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(normalizeEmail(e.target.value))}
                      className="h-9 w-full rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-base text-slate-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 lg:h-auto lg:rounded-xl lg:px-3.5 lg:py-3 lg:text-sm"
                      placeholder="you@example.com"
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="h-9 w-full rounded-[10px] bg-[#0d0b18] py-0 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 lg:h-auto lg:rounded-xl lg:py-3.5 lg:text-sm lg:font-semibold"
                    data-testid="forgot-submit"
                  >
                    {forgotLoading ? 'Sending…' : 'Send reset link'}
                  </button>

                  <button
                    type="button"
                    onClick={closeForgotPassword}
                    className="w-full text-center text-xs font-medium text-slate-500 transition-colors hover:text-violet-600"
                  >
                    ← Back to login
                  </button>
                </form>
              )
            ) : oauthProfile ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="oauth-role-picker">
                <p className="text-sm leading-6 text-slate-500">
                  We found your details from {oauthProfile.provider ? oauthProfile.provider.charAt(0).toUpperCase() + oauthProfile.provider.slice(1) : 'your SSO provider'}. Confirm how you want to use FounderBoard.
                </p>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[10px] font-bold uppercase leading-[13px] tracking-[0.08em] text-slate-600 lg:mb-1.5 lg:leading-[normal] lg:tracking-[0.13em]">
                      Full name
                    </label>
                    <input
                      type="text"
                      value={oauthProfile.full_name || ''}
                      readOnly
                      className="w-full cursor-default rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-900 outline-none"
                      data-testid="oauth-prefilled-name"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[10px] font-bold uppercase leading-[13px] tracking-[0.08em] text-slate-600 lg:mb-1.5 lg:leading-[normal] lg:tracking-[0.13em]">
                      Email address
                    </label>
                    <input
                      type="text"
                      value={oauthProfile.email || ''}
                      readOnly
                      className="w-full cursor-default rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-900 outline-none"
                      data-testid="oauth-prefilled-email"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">I am a</label>
                  <div className={`grid ${isProgramApplyFlow ? 'grid-cols-1' : 'grid-cols-3'} h-9 rounded-[10px] bg-slate-100 p-0.5 lg:h-auto lg:rounded-xl lg:p-1`}>
                    {!isProgramApplyFlow && (
                      <>
                        <button
                          type="button"
                          onClick={() => setOauthRole({ role: 'vc', entity_type: 'vc' })}
                          className={`h-8 rounded-lg px-2 py-0 text-xs font-semibold leading-4 transition-all lg:h-auto lg:py-2.5 lg:text-[11px] lg:leading-[normal] ${
                            oauthRole.role === 'vc' && oauthRole.entity_type === 'vc'
                              ? 'bg-violet-600 text-white shadow-sm'
                              : 'text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          VC / Angel
                        </button>
                        <button
                          type="button"
                          onClick={() => setOauthRole({ role: 'vc', entity_type: 'incubator' })}
                          className={`h-8 rounded-lg px-2 py-0 text-xs font-semibold leading-4 transition-all lg:h-auto lg:py-2.5 lg:text-[11px] lg:leading-[normal] ${
                            oauthRole.role === 'vc' && oauthRole.entity_type === 'incubator'
                              ? 'bg-violet-600 text-white shadow-sm'
                              : 'text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          Incubator
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => setOauthRole({ role: 'startup', entity_type: 'startup' })}
                      className={`h-8 rounded-lg px-2 py-0 text-xs font-semibold leading-4 transition-all lg:h-auto lg:py-2.5 lg:text-[11px] lg:leading-[normal] ${
                        oauthRole.role === 'startup'
                          ? 'bg-violet-600 text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-900'
                      }`}
                    >
                      Startup
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={finishOAuthSignup}
                  disabled={oauthBusy}
                  className="mt-5 w-full rounded-xl bg-[#0d0b18] py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="oauth-role-continue"
                >
                  {oauthBusy ? 'Please wait...' : 'Continue'}
                </button>
                <button
                  type="button"
                  onClick={() => setOauthProfile(null)}
                  className="mt-3 w-full text-center text-xs font-medium text-slate-500 transition-colors hover:text-violet-600"
                >
                  Cancel
                </button>
              </div>
            ) : signupSentTo ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm" data-testid="signup-sent-card">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-2xl">✉️</div>
                <p className="text-sm leading-6 text-slate-500">
                  Click the verification link in the email (it expires in 24 hours) and then log in.
                </p>
                <button
                  onClick={() => { setAuthTransition('signin-down'); setSignupSentTo(''); setEmailError(''); setEmailTouched(false); setIsLogin(true); }}
                  className="mt-6 text-sm font-semibold text-violet-600 hover:text-violet-700"
                  data-testid="signup-sent-back"
                >
                  ← Back to login
                </button>
                <div className="mt-6 text-xs text-slate-500">
                  Didn't get it?{' '}
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="font-semibold text-violet-600 hover:underline"
                    data-testid="signup-sent-resend"
                  >
                    {resending ? 'Sending…' : 'Resend verification'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-2.5 lg:space-y-5" data-testid="auth-form">
                  {!isLogin && (
                    <>
                      <div className="h-9 rounded-[10px] bg-slate-100 p-0.5 lg:h-auto lg:rounded-xl lg:p-1">
                        <div className={`grid h-full ${isProgramApplyFlow ? 'grid-cols-1' : 'grid-cols-3'} gap-0.5 lg:h-auto lg:gap-1`} aria-label="Account type">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, role: 'startup', entity_type: 'startup' })}
                            className={`h-8 rounded-lg px-2 py-0 text-xs font-semibold leading-4 transition-all lg:h-auto lg:py-2.5 lg:text-[11px] lg:leading-[normal] ${
                              formData.role === 'startup'
                                ? 'bg-violet-600 text-white shadow-sm'
                                : 'text-slate-500 hover:text-slate-900'
                            }`}
                            data-testid="role-startup-button"
                          >
                            Startup
                          </button>
                          {!isProgramApplyFlow && (
                            <>
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, role: 'vc', entity_type: 'vc' })}
                                className={`h-8 rounded-lg px-2 py-0 text-xs font-semibold leading-4 transition-all lg:h-auto lg:py-2.5 lg:text-[11px] lg:leading-[normal] ${
                                  formData.role === 'vc' && formData.entity_type === 'vc'
                                    ? 'bg-violet-600 text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-900'
                                }`}
                                data-testid="role-vc-button"
                              >
                                VC / Angel
                              </button>
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, role: 'vc', entity_type: 'incubator' })}
                                className={`h-8 rounded-lg px-2 py-0 text-xs font-semibold leading-4 transition-all lg:h-auto lg:py-2.5 lg:text-[11px] lg:leading-[normal] ${
                                  formData.role === 'vc' && formData.entity_type === 'incubator'
                                    ? 'bg-violet-600 text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-900'
                                }`}
                                data-testid="role-incubator-button"
                              >
                                Incubator
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase leading-[13px] tracking-[0.08em] text-slate-600 lg:mb-1.5 lg:leading-[normal] lg:tracking-[0.13em]" data-testid="full-name-label">
                          Full name
                        </label>
                        <input
                          type="text"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value.replace(/[^\p{L}\s]/gu, '') })}
                          className="h-9 w-full rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-base text-slate-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 lg:h-auto lg:rounded-xl lg:px-3.5 lg:py-3 lg:text-sm"
                          placeholder="John Doe"
                          required={!isLogin}
                          data-testid="full-name-input"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="mb-1 block text-[10px] font-bold uppercase leading-[13px] tracking-[0.08em] text-slate-600 lg:mb-1.5 lg:leading-[normal] lg:tracking-[0.13em]" data-testid="email-label">
                      Email address
                    </label>
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => {
                        const email = normalizeEmail(e.target.value);
                        setFormData({ ...formData, email });
                        setEmailError(validateEmailFriendly(email));
                      }}
                      onBlur={() => setEmailTouched(true)}
                      className="h-9 w-full rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-base text-slate-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 lg:h-auto lg:rounded-xl lg:px-3.5 lg:py-3 lg:text-sm"
                      placeholder="you@example.com"
                      required
                      aria-invalid={showEmailError}
                      data-testid="email-input"
                    />
                    {showEmailError && (
                      <p className="mt-1.5 text-xs font-medium text-red-600" data-testid="email-error">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <div className="mb-1 flex items-center justify-between gap-4 lg:mb-1.5">
                      <label className="block text-[10px] font-bold uppercase leading-[13px] tracking-[0.08em] text-slate-600 lg:leading-[normal] lg:tracking-[0.13em]" data-testid="password-label">
                        Password
                      </label>
                      {isLogin && (
                        <button
                          type="button"
                          onClick={openForgotPassword}
                          className="text-[11px] font-semibold text-violet-600 hover:text-violet-700"
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="h-9 w-full rounded-[10px] border border-slate-200 bg-slate-50 px-3 text-base text-slate-900 outline-none transition focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 lg:h-auto lg:rounded-xl lg:px-3.5 lg:py-3 lg:text-sm"
                      placeholder="••••••••"
                      required
                      data-testid="password-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="h-9 w-full rounded-[10px] bg-[#0d0b18] py-0 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 lg:h-auto lg:rounded-xl lg:py-3.5 lg:text-sm lg:font-semibold"
                    data-testid="submit-button"
                  >
                    {loading ? 'Please wait...' : (isLogin ? 'Log in' : 'Create account')}
                  </button>

                  {showResendVerify && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center text-xs text-amber-800" data-testid="resend-verify-banner">
                      Your email isn't verified yet.{' '}
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resending}
                        className="font-semibold underline"
                        data-testid="resend-verify-button"
                      >
                        {resending ? 'Sending…' : 'Resend verification email'}
                      </button>
                    </div>
                  )}
                </form>

                {!signupSentTo && !oauthProfile && (GOOGLE_SIGNIN_ENABLED || OAUTH_PROVIDER_ENABLED.microsoft || OAUTH_PROVIDER_ENABLED.zoho || OAUTH_PROVIDER_ENABLED.linkedin) && (
                  <div className="mt-4 lg:mt-8">
                    <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.12em] text-slate-400" aria-hidden="true">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span>{isLogin ? 'or sign in with' : 'or continue with'}</span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <div className="mt-2.5 flex min-h-9 items-center justify-center gap-3 rounded-[10px] border border-slate-200 bg-white px-4 py-2 lg:mt-5 lg:min-h-0 lg:gap-4 lg:rounded-xl lg:px-5 lg:py-3.5">
                      {/* Keep every existing FounderBoard SSO provider and its original auth flow. */}
                      <GoogleSignInButton onCredential={handleGoogleCredential} disabled={oauthBusy || loading} />
                      <OAuthIconButton provider="microsoft" context="founder" loginPath="/login" disabled={oauthBusy || loading} />
                      <OAuthIconButton provider="zoho" context="founder" loginPath="/login" disabled={oauthBusy || loading} />
                      <OAuthIconButton provider="linkedin" context="founder" loginPath="/login" disabled={oauthBusy || loading} />
                    </div>
                  </div>
                )}

                <div className="mt-4 text-center lg:mt-8">
                  <button
                    onClick={toggleAuthMode}
                    className="text-xs text-slate-500 transition-colors hover:text-violet-700"
                    data-testid="toggle-auth-mode"
                  >
                    {isLogin ? (
                      <>Don't have an account? <span className="font-semibold text-violet-600">Create one</span></>
                    ) : (
                      <>Already have an account? <span className="font-semibold text-violet-600">Log in</span></>
                    )}
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
